import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Copy, KeyRound, RefreshCw, Trash2, Plug, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const ENDPOINTS = [
  ["POST", "/api/control/spin/attack", "Ruota Operatore Attacco"],
  ["POST", "/api/control/spin/defense", "Ruota Operatore Difesa"],
  ["POST", "/api/control/spin/penalty", "Ruota Penitenze"],
  ["POST", "/api/control/spin/custom1", "Ruota Custom 1"],
  ["POST", "/api/control/spin/custom2", "Ruota Custom 2"],
  ["POST", "/api/control/objective/easy", "Obiettivo Facile"],
  ["POST", "/api/control/objective/medium", "Obiettivo Medio"],
  ["POST", "/api/control/objective/hard", "Obiettivo Difficile"],
  ["POST", "/api/control/overlay/hide", "Nascondi Overlay"],
  ["POST", "/api/control/overlay/reset", "Reset Overlay"],
  ["POST", "/api/control/simulate-sub", "Simula Sub"],
];

export default function StreamDeckGuide() {
  const [key, setKey] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [ping, setPing] = useState(null);

  const load = useCallback(async () => {
    const s = await api.getSettings();
    setKey(s.streamdeck?.apiKey || null);
  }, []);
  useEffect(() => { load(); }, [load]);

  const gen = async () => { const r = await api.generateKey(); setKey(r.apiKey); setReveal(true); toast.success("API Key generata"); };
  const revoke = async () => { if (!window.confirm("Revocare la API Key? Lo Stream Deck smetterà di funzionare.")) return; await api.revokeKey(); setKey(null); toast.success("API Key revocata"); };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiato"); };

  const testPing = async () => {
    if (!key) return;
    try {
      const res = await fetch(`${API}/control/ping`, { headers: { "X-API-Key": key } });
      setPing(res.ok);
      res.ok ? toast.success("Connessione OK") : toast.error("API key non valida");
    } catch { setPing(false); toast.error("Connessione fallita"); }
  };

  return (
    <div className="p-5 md:p-8 max-w-5xl mx-auto">
      <PageHeader title="Stream Deck" subtitle="Controlla l'app dal tuo Elgato Stream Deck tramite API autenticata." accent="amber" />

      <div className="bg-[#131722] border border-[#334155] rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 text-white font-display font-bold uppercase mb-4"><KeyRound className="w-5 h-5 text-amber-400" /> API Key</div>
        {key ? (
          <div className="flex flex-wrap gap-2 items-center">
            <Input readOnly type={reveal ? "text" : "password"} value={key} className="bg-[#0b0e16] border-[#334155] text-amber-300 font-mono flex-1 min-w-[220px]" data-testid="streamdeck-api-key" />
            <Button size="icon" variant="outline" onClick={() => setReveal(!reveal)} className="border-slate-600 text-slate-200">{reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</Button>
            <Button size="icon" onClick={() => copy(key)} className="bg-amber-500 hover:bg-amber-400 text-[#080a0f]" data-testid="copy-api-key"><Copy className="w-4 h-4" /></Button>
            <Button onClick={gen} variant="outline" className="border-slate-600 text-slate-200" data-testid="regenerate-api-key"><RefreshCw className="w-4 h-4 mr-2" /> Rigenera</Button>
            <Button onClick={revoke} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid="revoke-api-key"><Trash2 className="w-4 h-4 mr-2" /> Revoca</Button>
          </div>
        ) : (
          <Button onClick={gen} className="bg-amber-500 hover:bg-amber-400 text-[#080a0f]" data-testid="generate-api-key"><KeyRound className="w-4 h-4 mr-2" /> Genera API Key</Button>
        )}
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <div className="text-xs text-slate-400">URL API:</div>
          <code className="bg-[#0b0e16] border border-[#334155] rounded px-3 py-1.5 text-amber-300 font-mono text-sm">{API}</code>
          <Button size="icon" variant="outline" onClick={() => copy(API)} className="border-slate-600 text-slate-200 h-8 w-8"><Copy className="w-3.5 h-3.5" /></Button>
          <Button onClick={testPing} disabled={!key} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="test-streamdeck-connection"><Plug className="w-4 h-4 mr-2" /> Test connessione</Button>
          {ping === true && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
          {ping === false && <XCircle className="w-5 h-5 text-red-400" />}
        </div>
        <p className="text-slate-500 text-xs mt-3">La chiave non è mai esposta nell'overlay. Invia l'header <code className="text-amber-400">X-API-Key</code> con ogni richiesta.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#131722] border border-[#334155] rounded-xl p-5">
          <h3 className="font-display font-bold uppercase tracking-wide text-white mb-3">Installazione plugin</h3>
          <ol className="text-slate-300 text-sm space-y-1.5 list-decimal list-inside">
            <li>Compila e installa il plugin dalla cartella <code className="text-amber-400">/streamdeck-plugin</code> (vedi README)</li>
            <li>Inserisci l'URL della web app nel Property Inspector</li>
            <li>Inserisci la API Key qui sopra</li>
            <li>Trascina le azioni sui pulsanti Stream Deck</li>
            <li>Premi "Test connessione"</li>
          </ol>
          <p className="text-slate-500 text-xs mt-3">Il codice sorgente completo del plugin (manifest, azioni, Property Inspector, profilo di esempio) è incluso nel repository.</p>
        </div>

        <div className="bg-[#131722] border border-[#334155] rounded-xl p-5">
          <h3 className="font-display font-bold uppercase tracking-wide text-white mb-3">Endpoint disponibili</h3>
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {ENDPOINTS.map(([m, path, label]) => (
              <div key={path} className="flex items-center gap-2 text-sm">
                <span className="text-[10px] font-bold bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">{m}</span>
                <code className="text-amber-300 font-mono text-xs truncate">{path}</code>
                <span className="text-slate-500 text-xs ml-auto hidden sm:inline">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
